import { useQuery } from "@tanstack/react-query";

import { SpcInstance } from "@/instance/axios";
import {
  InspectionsGetRequestInspectionTypeEnum,
  SpecificationsGet200ResponseRowsInner,
} from "@sizlcorp/sizl-api-document/dist/models";
import { AxiosResponse } from "axios";

export type InspectionSpecificationOption = {
  value: string;
  label: string;
  id: number;
};

export const useInspectionSpecificationsAutoComplete = (
  focus = false,
  keyword = "",
  itemCode: string | undefined,
  routingCode: string | undefined,
  inspectionType: InspectionsGetRequestInspectionTypeEnum,
  specificationIds?: number[] | null,
  options?: {
    onSuccess: (data: InspectionSpecificationOption[]) => void;
  },
) => {
  return useQuery(
    ["inspectSpecification-get-autocomplete-keyword", keyword],
    () =>
      SpcInstance.specificationsFindPost({
        search: keyword,
        searchFields: ["title", "code"],
        query: JSON.stringify({
          $and: [
            { inspectionType: inspectionType },
            ...(itemCode
              ? [
                  {
                    itemCode: {
                      $or: [itemCode, null],
                    },
                  },
                ]
              : []),
            ...(routingCode
              ? [
                  {
                    routingCode: {
                      $or: [routingCode, null],
                    },
                  },
                ]
              : []),
            ...(specificationIds && specificationIds.length > 0
              ? [
                  {
                    id: {
                      $in: specificationIds,
                    },
                  },
                ]
              : []),
          ],
        }),
      }).then(
        (res: AxiosResponse<SpecificationsGet200ResponseRowsInner[]>) =>
          res.data,
      ),
    {
      enabled: focus,
      select: (
        data: SpecificationsGet200ResponseRowsInner[],
      ): InspectionSpecificationOption[] =>
        data.map((InspectionSpecification) => {
          return {
            value: InspectionSpecification.code ?? "",
            label: InspectionSpecification.title as string,
            id: (InspectionSpecification as any).id as number,
          };
        }) ?? [],
      onSuccess: options?.onSuccess,
    },
  );
};

const getSpecificationByCode = (code: string | null) =>
  SpcInstance.specificationsGet({
    ...(code && { search: code }),
    searchFields: ["code"],
  }).then((res) => res.data.rows ?? []);

export const useInspectSpecificationsCode = (
  enabled: boolean = false,
  code: string | null,
  options?: {
    onSuccess: (data?: InspectionSpecificationOption[]) => void;
  },
) => {
  return useQuery(
    ["inspectSpecification-get-code", code],
    () => getSpecificationByCode(code),
    {
      enabled,
      select: (data: SpecificationsGet200ResponseRowsInner[]) =>
        data?.map((specification) => {
          return {
            value: specification.code ?? "",
            label: specification.title as string,
            id: (specification as any).id as number,
          };
        }),
      onSuccess: options?.onSuccess,
    },
  );
};
